import React, { useEffect } from 'react';
import { useLanguage } from '../../src/functions/useLanguage';
import { wt } from '../i18n';

const META = {
  title: 'Privacy Policy | KodaCare',
  description:
    'KodaCare Privacy Policy — how we collect, process, and protect your personal and pet health data in accordance with GDPR and applicable data protection regulations.',
  lastUpdated: '10 May 2026',
};

export function PrivacyPolicy() {
  const { language } = useLanguage();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);

  useEffect(() => {
    const prev = document.title;
    document.title = META.title;

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.content;
    metaDesc.content = META.description;

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const canonicalAdded = !canonical;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://kodacare.pet/privacy';

    return () => {
      document.title = prev;
      if (metaDesc) metaDesc.content = prevDesc;
      if (canonicalAdded && canonical) canonical.remove();
    };
  }, []);

  return (
    <main className="max-w-3xl mx-auto px-6 py-16 text-gray-800">
      <h1 className="text-3xl font-black text-gray-900 mb-2">{t('legal.privacyTitle')}</h1>
      <p className="text-sm text-gray-400 mb-10">{t('legal.lastUpdated')}: {META.lastUpdated}</p>

      <article className="space-y-8 text-sm leading-relaxed">

        {/* ── 1 ── */}
        <Section id="1" title="1. Who We Are">
          <p>
            KodaCare is a pet health companion application developed and operated by <strong>The Wellhouse Studio</strong>
            {' '}("we", "us", "our"). KodaCare is designed to help pet parents track daily health metrics, manage
            medications and care routines, organise vet records, and access practical guidance for their companions.
            It does not perform veterinary diagnosis, prognosis, or clinical decision-making.
          </p>
          <p>
            <strong>Data Controller:</strong><br />
            The Wellhouse Studio
          </p>
        </Section>

        {/* ── 2 ── */}
        <Section id="2" title="2. Scope of This Policy">
          <p>
            This Privacy Policy applies to all personal data processed through the KodaCare web application and
            Progressive Web App (PWA), including data submitted by users located in the European Economic Area (EEA),
            the United Kingdom, and other jurisdictions. It is written in compliance with:
          </p>
          <ul>
            <li>Regulation (EU) 2016/679 — General Data Protection Regulation (<strong>GDPR</strong>)</li>
            <li>UK GDPR and the Data Protection Act 2018</li>
            <li>ISO/IEC 27001 information security principles</li>
            <li>ePrivacy Directive 2002/58/EC (Cookie Law)</li>
          </ul>
        </Section>

        {/* ── 3 ── */}
        <Section id="3" title="3. Data We Collect">
          <h3>3.1 Account Data</h3>
          <ul>
            <li>Full name, email address, encrypted password (via Firebase Authentication)</li>
            <li>Pet profiles: name, species, breed, age, and health notes (optional)</li>
            <li>Account creation timestamp and last sign-in time</li>
          </ul>

          <h3>3.2 Pet Health Data</h3>
          <p>
            KodaCare processes the following health data you voluntarily enter on behalf of your pet:
          </p>
          <ul>
            <li>Vital signs: weight, temperature, water intake, food intake, heart rate, respiratory rate, mucous membrane colour, and other vet-relevant observations</li>
            <li>Medication names, dosages, schedules, and adherence logs</li>
            <li>Vet appointment dates, contacts, and uploaded records</li>
            <li>Koda XP points, streaks, and activity data (rewards system)</li>
          </ul>
          <p>
            <strong>Roomies feature:</strong> If you opt into the Roomies group feature, limited activity signals
            (e.g. "logged vitals today", "7-day streak reached") are shared with other members of your group. Raw
            health values, medication details and personal identifiers are never shared via Roomies.
          </p>

          <h3>3.3 Usage and Technical Data</h3>
          <ul>
            <li>Device type, operating system, browser version</li>
            <li>Session timestamps, feature interaction events (analytics only — no health data tied to analytics)</li>
            <li>IP address (pseudonymised; not stored beyond session)</li>
          </ul>

          <h3>3.4 Cookie Data</h3>
          <p>See Section 10 (Cookies) for full detail.</p>
        </Section>

        {/* ── 4 ── */}
        <Section id="4" title="4. Legal Basis for Processing">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 border border-gray-200 font-semibold">Data Category</th>
                <th className="text-left p-3 border border-gray-200 font-semibold">Legal Basis</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Account data (name, email)', 'Art. 6(1)(b) GDPR — Performance of contract'],
                ['Health data (vitals, meds)', 'Art. 9(2)(a) GDPR — Explicit consent'],
                ['Health data (safety / MDR compliance)', 'Art. 9(2)(i) GDPR — Public health / medical device obligations'],
                ['Analytics and usage data', 'Art. 6(1)(f) GDPR — Legitimate interests (product improvement)'],
                ['Cookie consent records', 'Art. 6(1)(c) GDPR — Legal obligation (ePrivacy Directive)'],
                ['Roomies activity signals', 'Art. 9(2)(a) GDPR — Explicit consent (opt-in toggle)'],
              ].map(([cat, basis]) => (
                <tr key={cat} className="odd:bg-white even:bg-gray-50">
                  <td className="p-3 border border-gray-200">{cat}</td>
                  <td className="p-3 border border-gray-200">{basis}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {/* ── 5 ── */}
        <Section id="5" title="5. How We Use Your Data">
          <ul>
            <li>Providing and securing your KodaCare account and pet health logbook</li>
            <li>Sending medication and care-routine reminders you have configured</li>
            <li>Computing trends and presenting insights from your own logged data</li>
            <li>Operating the Koda XP and rewards system (streaks, milestones)</li>
            <li>Facilitating opt-in Roomies group activity feeds</li>
            <li>Improving application performance and user experience (aggregated, anonymised analytics only)</li>
            <li>Responding to support requests and exercising legal rights</li>
          </ul>
          <p>
            We do <strong>not</strong> use your data for advertising profiling, sell data to third parties, or
            use health data to train AI/ML models without separate explicit consent.
          </p>
        </Section>

        {/* ── 6 ── */}
        <Section id="6" title="6. Data Sharing and Sub-Processors">
          <p>
            We disclose personal data only to the following categories of recipients, each bound by GDPR-compliant
            Data Processing Agreements (DPAs):
          </p>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 border border-gray-200 font-semibold">Processor</th>
                <th className="text-left p-3 border border-gray-200 font-semibold">Purpose</th>
                <th className="text-left p-3 border border-gray-200 font-semibold">Location</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Google Firebase (Auth, Firestore, Cloud Functions)', 'Authentication, encrypted database storage, push notifications', 'EU (eur3 region) / Standard Contractual Clauses'],
                ['Google Analytics (anonymised)', 'Aggregated usage analytics', 'EU SCCs — IP anonymisation enabled'],
                ['Stripe (if applicable)', 'Payment processing (no health data shared)', 'EU / PCI-DSS compliant'],
              ].map(([proc, purpose, loc]) => (
                <tr key={proc} className="odd:bg-white even:bg-gray-50">
                  <td className="p-3 border border-gray-200 font-medium">{proc}</td>
                  <td className="p-3 border border-gray-200">{purpose}</td>
                  <td className="p-3 border border-gray-200 text-xs">{loc}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-sm text-gray-500 mt-2">
            No health data is ever transferred to sub-processors for purposes other than those listed above.
          </p>
        </Section>

        {/* ── 7 ── */}
        <Section id="7" title="7. International Data Transfers">
          <p>
            All health and account data is stored in <strong>Google Cloud — EU (eur3)</strong> multi-region
            infrastructure located within the EEA. Where any ancillary data processing involves transfers outside
            the EEA (e.g. support tooling), we rely on <strong>Standard Contractual Clauses (SCCs)</strong> approved
            by the European Commission under Decision 2021/914/EU, supplemented by transfer impact assessments where
            required.
          </p>
        </Section>

        {/* ── 8 ── */}
        <Section id="8" title="8. Data Retention">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 border border-gray-200 font-semibold">Data Type</th>
                <th className="text-left p-3 border border-gray-200 font-semibold">Retention Period</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Active account data (vitals, meds, contacts)', 'Retained for the life of the account'],
                ['Account data after deletion request', 'Deleted within 30 days; anonymised aggregate logs retained up to 6 months for MDR obligations'],
                ['Authentication logs', '90 days (security purposes)'],
                ['Analytics data (anonymised)', 'Up to 26 months'],
                ['Cookie consent records', '12 months from consent date'],
                ['Support correspondence', '3 years from closure'],
              ].map(([type, period]) => (
                <tr key={type} className="odd:bg-white even:bg-gray-50">
                  <td className="p-3 border border-gray-200">{type}</td>
                  <td className="p-3 border border-gray-200">{period}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3">
            Under EU MDR Article 10(8), certain post-market surveillance records may need to be retained for a
            minimum of 10 years after the last device was placed on the market. These records are anonymised and
            contain no personally identifiable information.
          </p>
        </Section>

        {/* ── 9 ── */}
        <Section id="9" title="9. Your Rights Under GDPR">
          <p>As a data subject under GDPR, you have the following rights:</p>
          <ul>
            <li><strong>Right of access (Art. 15)</strong> — Request a copy of all personal data we hold about you.</li>
            <li><strong>Right to rectification (Art. 16)</strong> — Correct inaccurate or incomplete data.</li>
            <li><strong>Right to erasure / "right to be forgotten" (Art. 17)</strong> — Request deletion of your account and all associated data. You can initiate this from Settings → Security → Delete Account, or by emailing us.</li>
            <li><strong>Right to restriction of processing (Art. 18)</strong> — Request that we limit how we use your data while a dispute is resolved.</li>
            <li><strong>Right to data portability (Art. 20)</strong> — Export all your logged data in a machine-readable format (CSV) via Settings → Export Data.</li>
            <li><strong>Right to object (Art. 21)</strong> — Object to processing based on legitimate interests, including analytics.</li>
            <li><strong>Right to withdraw consent (Art. 7(3))</strong> — Withdraw consent for health data processing at any time. Withdrawal does not affect the lawfulness of prior processing.</li>
            <li><strong>Rights related to automated decision-making (Art. 22)</strong> — KodaCare does not make solely automated decisions with legal or significant effects.</li>
          </ul>
          <p>
            To exercise any of these rights, contact us via the link in Section 15. We will respond within{' '}
            <strong>30 days</strong>. If you believe your rights have been violated, you have the right to lodge a
            complaint with your national supervisory authority (e.g. ICO in the UK, CNIL in France, BfDI in Germany).
          </p>
        </Section>

        {/* ── 10 ── */}
        <Section id="10" title="10. Cookies">
          <p>KodaCare uses the following categories of cookies:</p>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 border border-gray-200 font-semibold">Category</th>
                <th className="text-left p-3 border border-gray-200 font-semibold">Purpose</th>
                <th className="text-left p-3 border border-gray-200 font-semibold">Can opt out?</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Strictly necessary', 'Authentication session tokens, language preference, cookie consent record', 'No — required for the app to function'],
                ['Analytics (Google Analytics)', 'Aggregated, anonymised usage statistics — IP anonymisation enabled; no health data', 'Yes — via consent banner ("Essential only")'],
                ['Performance / PWA', 'Service worker caching to support offline functionality', 'No — required for PWA operation'],
              ].map(([cat, purpose, opt]) => (
                <tr key={cat} className="odd:bg-white even:bg-gray-50">
                  <td className="p-3 border border-gray-200 font-medium">{cat}</td>
                  <td className="p-3 border border-gray-200">{purpose}</td>
                  <td className="p-3 border border-gray-200">{opt}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3">
            Your cookie preference is stored locally under the key <code>kodacare_cookie_consent</code> and expires
            after 12 months. You can change your preference at any time by clearing your browser's local storage.
          </p>
        </Section>

        {/* ── 11 ── */}
        <Section id="11" title="11. Security Measures">
          <p>
            We apply appropriate technical and organisational measures to protect your data, including:
          </p>
          <ul>
            <li><strong>Encryption in transit</strong> — All data is transmitted over TLS 1.2+.</li>
            <li><strong>Encryption at rest</strong> — Firestore data is encrypted at rest by Google Cloud using AES-256.</li>
            <li><strong>Authentication</strong> — Firebase Authentication with email-verified accounts; password hashing via bcrypt.</li>
            <li><strong>Access controls</strong> — Firestore security rules enforce per-user data isolation. No staff member has routine access to individual health logs.</li>
            <li><strong>Security testing</strong> — Regular dependency auditing; OWASP Top 10 review prior to major releases.</li>
            <li><strong>Breach notification</strong> — In the event of a personal data breach affecting EU residents, we will notify the relevant supervisory authority within <strong>72 hours</strong> and affected users without undue delay, as required by GDPR Article 33–34.</li>
          </ul>
        </Section>

        {/* ── 12 ── */}
        <Section id="12" title="12. Third-Party Veterinary Disclaimer">
          <p>
            KodaCare is a pet health tracking and management tool. It is not a licensed veterinary service and does
            not provide medical advice, diagnosis, or treatment recommendations. All health data you enter belongs to
            you and is used solely to provide the features you request. We recommend always consulting a qualified
            veterinarian for decisions about your pet's health and care.
          </p>
        </Section>

        {/* ── 13 ── */}
        <Section id="13" title="13. Children's Data">
          <p>
            KodaCare is not intended for use by children under the age of 16. We do not knowingly collect personal
            data from users under 16. If you believe a child has registered an account, please contact us using the
            details in Section 15 and we will delete the account promptly.
          </p>
        </Section>

        {/* ── 14 ── */}
        <Section id="14" title="14. Changes to This Policy">
          <p>
            We may update this Privacy Policy to reflect changes in our practices, legal requirements, or regulatory
            guidance. When we make material changes, we will:
          </p>
          <ul>
            <li>Update the "Last updated" date at the top of this page</li>
            <li>Notify registered users by in-app notification at next login</li>
            <li>Where required by law, re-request consent</li>
          </ul>
          <p>
            Continued use of KodaCare after the effective date of an updated Policy constitutes acceptance of the
            changes, except where re-consent is legally required.
          </p>
        </Section>

      </article>
    </main>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={`section-${id}`}>
      <h2 className="text-lg font-bold mb-2">{title}</h2>
      <div className="text-gray-700 leading-relaxed space-y-4">{children}</div>
    </section>
  );
}
